import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { BarChart3, CheckCircle, Eye, EyeOff, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';

import verdLogo from '../../images/verd_logo_new.jpeg';
import verdLogoWhite from '../../images/verd_logo_transparent_white.png';

import * as Constants from '../Constant';
import axios from 'axios';
import PropTypes from 'prop-types';

const Login = () => {
  const history = useHistory();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();

    try {
      setIsLoading(true);

      var payload = {
        email: email,
        password: password
      };

      const url = Constants.BASE_URL + 'employee/login';

      const response = await axios.post(url, payload);

      const displayName = response?.data?.employee.DisplayName;
      const userId = response?.data?.employee._id;
      const access = response?.data?.employee.type === 'employee' ? 2 : 1;

      // eslint-disable-next-line no-undef
      localStorage.setItem('loginTime', Date.now());
      // eslint-disable-next-line no-undef
      localStorage.setItem('token', response?.data?.token);
      // eslint-disable-next-line no-undef
      localStorage.setItem('name', displayName);
      // eslint-disable-next-line no-undef
      localStorage.setItem('access', access);
      // eslint-disable-next-line no-undef
      localStorage.setItem('userId', userId);
      // eslint-disable-next-line no-undef
      localStorage.setItem(
        'role',
        response?.data?.employee['user_type']
          ? response?.data?.employee['user_type']
          : response?.data?.employee['type']
          ? response?.data?.employee['type']
          : ''
      );

      history.push('/');
    } catch (error) {
      setLoginError(error?.response?.data?.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="tw-min-h-screen tw-bg-background tw-flex">
      {/* Left Panel - Branding */}
      <div className="tw-hidden lg:tw-flex lg:tw-w-1/2 tw-bg-[#2b2b2b] tw-text-[#FAFAFA] tw-flex-col tw-justify-between tw-p-12">
        <div>
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-h-8 tw-rounded-lg tw-bg-primary-foreground/10 tw-flex tw-items-center tw-justify-center">
              <img src={verdLogoWhite} className="tw-h-full tw-w-full tw-object-contain" alt="Logo" />
            </div>
          </div>
        </div>

        <div className="tw-space-y-8">
          <div>
            <h1 className="tw-text-4xl tw-font-semibold tw-tracking-tight tw-leading-tight tw-text-balance tw-text-[#FAFAFAB3]">
              Effortless time tracking for modern teams
            </h1>
            <p className="tw-mt-4 tw-text-[#FAFAFAB3] tw-text-lg tw-leading-relaxed">
              Track hours, manage projects, and streamline payroll with our intuitive timesheet platform.
            </p>
          </div>

          <div className="tw-grid tw-gap-4">
            <FeatureItem
              icon={<BarChart3 className="tw-w-5 tw-h-5" />}
              title="Real-time Asset management"
              description="Insights into real time assets"
            />
            <FeatureItem
              icon={<Users className="tw-w-5 tw-h-5" />}
              title="Team Management"
              description="Organize projects and assignments"
            />
            <FeatureItem
              icon={<CheckCircle className="tw-w-5 tw-h-5" />}
              title="Approval Workflows"
              description="Streamlined timesheet approvals"
            />
          </div>
        </div>

        <p className="tw-text-sm tw-text-[#FAFAFA80]">Trusted by 10+ companies</p>
      </div>

      {/* Right Panel - Login Form */}
      <div className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-p-8">
        <div className="tw-w-full tw-max-w-md tw-space-y-8">
          {/* Mobile Logo */}
          <div>
            <div className="tw-flex tw-justify-center tw-items-center tw-gap-3">
              <div className="tw-h-8 tw-rounded-lg tw-bg-primary-foreground/10 tw-flex tw-items-center tw-justify-center">
                <img src={verdLogo} className="tw-h-full tw-w-full tw-object-contain" alt="Logo" />
              </div>
            </div>
          </div>

          <div className="tw-text-center lg:tw-text-left">
            <h2 className="tw-text-2xl tw-font-semibold tw-tracking-tight tw-text-foreground">Welcome back</h2>
            <p className="tw-mt-2 tw-text-muted-foreground">Sign in to continue to your dashboard</p>
          </div>

          <form onSubmit={handleLogin} className="tw-space-y-6">
            <div className="tw-space-y-4">
              <div className="tw-space-y-2">
                <Label htmlFor="email" className="tw-text-sm tw-font-medium tw-text-foreground">
                  Email address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="tw-h-12 tw-bg-card tw-border-input"
                  required
                />
              </div>

              <div className="tw-space-y-2">
                <Label htmlFor="password" className="tw-text-sm tw-font-medium tw-text-foreground">
                  Password
                </Label>
                <div className="tw-relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="tw-h-12 tw-pr-12 tw-bg-card tw-border-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="tw-absolute tw-right-4 tw-top-1/2 tw--translate-y-1/2 tw-text-muted-foreground hover:tw-text-foreground tw-transition-colors"
                  >
                    {showPassword ? <EyeOff className="tw-w-5 tw-h-5" /> : <Eye className="tw-w-5 tw-h-5" />}
                  </button>
                </div>
              </div>
            </div>

            {loginError && (
              <div className="tw-bg-destructive/10 tw-border tw-border-destructive/20 tw-text-destructive tw-rounded-lg tw-p-3 tw-text-sm">
                {loginError}
              </div>
            )}

            <div className="tw-flex tw-items-center tw-justify-between">
              <div className="tw-flex tw-items-center tw-gap-2">
                <Checkbox id="remember" />
                <Label htmlFor="remember" className="tw-text-sm tw-text-muted-foreground tw-cursor-pointer">
                  Remember me
                </Label>
              </div>
              <a
                href="#"
                className="tw-text-sm tw-font-medium tw-text-foreground hover:tw-text-foreground/80 tw-transition-colors"
              >
                Forgot password?
              </a>
            </div>

            <Button
              type="submit"
              className="tw-w-full tw-h-12 tw-bg-blue-700 tw-text-base tw-text-white tw-font-medium"
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>

          <div className="tw-text-center">
            <p className="tw-text-sm tw-text-muted-foreground">
              {"Don't have an account? "}
              <a
                href="#"
                className="tw-font-medium tw-text-foreground hover:tw-text-foreground/80 tw-transition-colors"
              >
                Contact your admin
              </a>
            </p>
            <p className="tw-mt-3 tw-text-sm tw-text-muted-foreground">
              <Link
                to="/privacy-policy"
                className="tw-font-medium tw-text-foreground hover:tw-text-foreground/80 tw-transition-colors"
              >
                Privacy Policy
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;

function FeatureItem({ icon, title, description }) {
  return (
    <div className="tw-flex tw-items-start tw-gap-4">
      <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-bg-primary-foreground/10 tw-flex tw-items-center tw-justify-center tw-shrink-0">
        {icon}
      </div>
      <div>
        <h3 className="tw-font-medium tw-text-[#FAFAFA99]">{title}</h3>
        <p className="tw-text-sm tw-text-[#FAFAFA99]">{description}</p>
      </div>
    </div>
  );
}

FeatureItem.propTypes = {
  icon: PropTypes.string,
  title: PropTypes.func,
  description: PropTypes.func
};
